import { ReactNode, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Colors, Icon } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";
import { translate } from "utils/string-table";

import {
  ModalSubFeedbackContentNegative,
  ModalSubFeedbackContentNeutral,
  ModalSubFeedbackContentPositive,
  ModalSubFeedbackContentRating,
} from "~components/ModalSubFeedback";
import { submitSubFeedback } from "~utils";

import "./ModalSubFeedback.css";

import { Button } from "~components/Molecules";

export type SubFeedbackRecipient = {
  id: string;
  name: string;
  profileImgUrl?: string;
  isSwingHero?: boolean;
};

export type SubmitFeedbackData = {
  rating: FeedbackRating;
  comments?: string;
  preferred: boolean;
  reasons:
    | Array<PositiveFeedbackReasons>
    | Array<NeutralFeedbackReasons>
    | Array<NegativeFeedbackReasons>;
  otherReason?: string;
  isBlocked: boolean;
};

type ModalSubFeedbackProps = {
  onDismiss: () => void;
  isFavoritedSub?: boolean;
  sub: SubFeedbackRecipient;
  schoolName: string;
  requestId: string;
  // pre-select an initial rating
  initialRating?: FeedbackRating;
};

/********* Component ********/
export function ModalSubFeedback(props: ModalSubFeedbackProps) {
  const { onDismiss, isFavoritedSub, sub, schoolName, requestId, initialRating } = props;

  /********* Hooks ********/
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("rating");
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating | undefined>(initialRating);
  const [_isFavoritedSub, _setIsFavoritedSub] = useState(isFavoritedSub);
  const [hasSubmissionError, setHasSubmissionError] = useState<boolean>();

  // Note: separate so we could memoize components
  const [positiveData, setPositiveData] = useState<PositiveData>({ reasons: [] });
  const [neutralData, setNeutralData] = useState<NeutralData>({ reasons: [] });
  const [negativeData, setNegativeData] = useState<NegativeData>({
    reasons: [],
    comment: "",
    isBlocked: undefined,
  });

  // Lifted state from <ModalSubFeedbackContentPositive /> so we can submit
  // early without going through the full positive flow.
  const [positiveStep, setPositiveStep] = useState<PositiveStep>("reasons");

  /********* Mutation ********/
  const { mutate: submitReview } = useMutation({
    mutationFn: (payload: SubmitFeedbackData) => {
      return submitSubFeedback(requestId, payload);
    },
    onSuccess: () => {
      setHasSubmissionError(false);
      queryClient.invalidateQueries();
    },
    onError: () => setHasSubmissionError(true),
  });

  /********* Handlers ********/
  function handleFormSubmit(feedbackData: PositiveData | NegativeData | NeutralData) {
    if (!feedbackRating) return;

    const comment = {
      RATING_POSITIVE: feedbackData.comment,
      RATING_NEGATIVE: feedbackData.comment,
      RATING_NEUTRAL: feedbackData.comment,
    }[feedbackRating];

    const otherReason = {
      RATING_POSITIVE: feedbackData.otherReason,
      RATING_NEGATIVE: feedbackData.otherReason,
      RATING_NEUTRAL: feedbackData.otherReason,
    }[feedbackRating];

    const feedbackPayload: SubmitFeedbackData = {
      rating: feedbackRating,
      preferred: _isFavoritedSub ?? false,
      isBlocked: feedbackRating === "RATING_NEGATIVE" && (negativeData.isBlocked ?? false),

      reasons: {
        RATING_POSITIVE: feedbackData.reasons,
        RATING_NEGATIVE: feedbackData.reasons,
        RATING_NEUTRAL: feedbackData.reasons,
      }[feedbackRating],

      // Only include otherReason if it exists
      ...(otherReason && { otherReason: otherReason }),

      // Only include comment if it exists
      ...(comment && { comments: comment }),
    };
    submitReview(feedbackPayload);
  }

  // This is used to allow admins to submit positive feedback
  // without going through the full form
  function submitPositiveRatingOnly() {
    if (!feedbackRating || feedbackRating !== "RATING_POSITIVE") return;

    const feedbackPayload: SubmitFeedbackData = {
      rating: feedbackRating,
      preferred: _isFavoritedSub ?? false,
      // Intentionally setting a reason to "other" since
      // the positive feedback form is not "complete"
      // and we would like to track that this is a quick review
      reasons: ["REASON_OTHER"],
      otherReason: "quick review",
      // isBlocked only applies to negative feedback
      // but required by the API, so setting to false
      isBlocked: false,
    };

    submitReview(feedbackPayload);
  }

  /********* Render ********/
  return (
    <div className="modal-wrapper">
      {step === "rating" && (
        <ModalSubFeedbackContentRating
          sub={sub}
          onDismiss={onDismiss}
          feedbackRating={feedbackRating}
          setFeedbackRating={setFeedbackRating}
          setStep={setStep}
          originalIsFavoritedSub={isFavoritedSub}
          isFavoritedSub={_isFavoritedSub}
          setIsFavoritedSub={_setIsFavoritedSub}
          submitPositiveRatingOnly={submitPositiveRatingOnly}
          hasSubmissionError={hasSubmissionError}
          setPositiveStep={setPositiveStep}
        />
      )}
      {step === "RATING_POSITIVE" && (
        <ModalSubFeedbackContentPositive
          onDismiss={onDismiss}
          positiveData={positiveData}
          setPositiveData={setPositiveData}
          setStep={setStep}
          subName={sub?.name}
          onSubmit={handleFormSubmit}
          hasSubmissionError={hasSubmissionError}
          positiveStep={positiveStep}
          setPositiveStep={setPositiveStep}
        />
      )}
      {step === "RATING_NEUTRAL" && (
        <ModalSubFeedbackContentNeutral
          onDismiss={onDismiss}
          neutralData={neutralData}
          setNeutralData={setNeutralData}
          setStep={setStep}
          subName={sub?.name}
          onSubmit={handleFormSubmit}
          hasSubmissionError={hasSubmissionError}
        />
      )}
      {step === "RATING_NEGATIVE" && (
        <ModalSubFeedbackContentNegative
          onDismiss={onDismiss}
          negativeData={negativeData}
          setNegativeData={setNegativeData}
          setStep={setStep}
          subName={sub?.name}
          schoolName={schoolName}
          onSubmit={handleFormSubmit}
          hasSubmissionError={hasSubmissionError}
        />
      )}
    </div>
  );
}

/********* Types ********/
export type FeedbackRating = "RATING_NEGATIVE" | "RATING_NEUTRAL" | "RATING_POSITIVE";
export type Step = "rating" | FeedbackRating;

export type PositiveFeedbackReasons =
  | "REASON_DISENGAGED"
  | "REASON_RELIABILITY"
  | "REASON_MANAGEMENT"
  | "REASON_KNOWLEDGEABLE"
  | "REASON_ATTITUDE"
  | "REASON_FLEXIBLE"
  | "REASON_OTHER";

export type NeutralFeedbackReasons =
  | "REASON_PHONE"
  | "REASON_DISENGAGED"
  | "REASON_DIRECTIONS"
  | "REASON_RELIABILITY"
  | "REASON_MANAGEMENT"
  | "REASON_ATTITUDE"
  | "REASON_OTHER";

export type NegativeFeedbackReasons =
  | "REASON_PHONE"
  | "REASON_DISENGAGED"
  | "REASON_DIRECTIONS"
  | "REASON_RELIABILITY"
  | "REASON_MANAGEMENT"
  | "REASON_SAFETY"
  | "REASON_ATTITUDE"
  | "REASON_OTHER";

export type PositiveStep = "reasons" | "comments" | "summary";

export type PositiveData = {
  reasons: Array<PositiveFeedbackReasons>;
  otherReason?: string;
  comment?: string;
};

export type NeutralData = {
  reasons: Array<NeutralFeedbackReasons>;
  otherReason?: string;
  comment?: string;
};

export type NegativeData = {
  reasons: Array<NegativeFeedbackReasons>;
  otherReason?: string;
  isBlocked: boolean | undefined;
  comment: string;
};

/********* Helper Components ********/
type ModalSubFeedbackHeaderProps = {
  onDismiss: (data?: string | number, role?: string) => void;
  title: string;
};

export function ModalSubFeedbackHeader(props: ModalSubFeedbackHeaderProps) {
  const { onDismiss, title } = props;
  return (
    <div className="modal-header">
      <h2>{title}</h2>
      <button onClick={() => onDismiss()} css={{ backgroundColor: "transparent" }}>
        <Icon name="Close" color={Colors.blue500} />
      </button>
    </div>
  );
}

type ModalSubFeedbackBodyProps = {
  children: ReactNode;
  hasApiError?: boolean;
};

export function ModalSubFeedbackBody(props: ModalSubFeedbackBodyProps) {
  const { children, hasApiError } = props;
  return (
    <div className="modal-body">
      {hasApiError && <AlertError />}
      {children}
    </div>
  );
}

type ModalSubFeedbackFooterProps = {
  buttonPrimary: {
    onClick?: () => void;
    isDisabled: boolean;
    text: string;
  };
  buttonSecondary?: {
    onClick?: () => void;
    isDisabled: boolean;
    text: string;
    hideIcon?: boolean;
  };
};

export function ModalSubFeedbackFooter(props: ModalSubFeedbackFooterProps) {
  const { buttonPrimary, buttonSecondary } = props;
  const layoutClassName = buttonSecondary ? "two-buttons" : "";

  return (
    <div className={`modal-footer ${layoutClassName}`}>
      {buttonPrimary && (
        <div className="action-button" key="primary">
          <Button
            isDisabled={buttonPrimary.isDisabled}
            label={buttonPrimary.text}
            onClick={() => buttonPrimary.onClick?.()}
            type="PRIMARY"
            isFluid
          />
        </div>
      )}
      {buttonSecondary && (
        <div className="action-button" key="secondary">
          <Button
            isDisabled={buttonSecondary.isDisabled}
            label={buttonSecondary.text}
            onClick={() => buttonSecondary.onClick?.()}
            type="SECONDARY"
            isFluid
          >
            {!buttonSecondary.hideIcon && <Icon name="Arrow Left" color={Colors.blue500} />}
          </Button>
        </div>
      )}
    </div>
  );
}

function AlertError() {
  return (
    <div css={{ marginBottom: 24 }}>
      <Alert color="danger" showIcon title={translate("LABEL_SOMETHING_WENT_WRONG")}>
        <span>
          {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
          <a
            target="_blank"
            rel="noreferrer"
            href={generateSwingSupportURL("/hc/en-us/requests/new")}
          >
            {translate("LABEL_CONTACT_SUPPORT")}
          </a>{" "}
          {translate("LABEL_CONTACT_SUPPORT_2")}
        </span>
      </Alert>
    </div>
  );
}
